(function () {
  if (window.lbMobileProductGalleryProgressInit) return;
  window.lbMobileProductGalleryProgressInit = true;

  const mobileMql = window.matchMedia('(max-width: 749px)');
  const swipeThreshold = 40;
  const instances = new WeakMap();

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function getSlides(instance) {
    return Array.from(instance.scroller.querySelectorAll('.product__media-item')).filter((slide) => slide.clientWidth > 0);
  }

  function getNearestIndex(instance, slides) {
    if (!slides.length) return 0;

    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - instance.scroller.scrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }

  function getActiveIndex(instance, slides = getSlides(instance)) {
    if (!slides.length) return 0;

    const currentPage = Number(instance.viewer.currentPage);
    if (Number.isFinite(currentPage) && currentPage >= 1 && currentPage <= slides.length) {
      return currentPage - 1;
    }

    return getNearestIndex(instance, slides);
  }

  function getInterpolatedProgress(instance, slides = getSlides(instance)) {
    if (slides.length <= 1) return 0;

    const lastIndex = slides.length - 1;
    const scrollLeft = instance.scroller.scrollLeft;
    const firstOffset = slides[0].offsetLeft;
    const lastOffset = slides[lastIndex].offsetLeft;

    if (scrollLeft <= firstOffset) return 0;
    if (scrollLeft >= lastOffset) return 1;

    for (let index = 0; index < lastIndex; index += 1) {
      const startOffset = slides[index].offsetLeft;
      const endOffset = slides[index + 1].offsetLeft;

      if (scrollLeft > endOffset) continue;

      const segmentWidth = endOffset - startOffset;
      if (segmentWidth <= 0) {
        return index / lastIndex;
      }

      const segmentProgress = clamp((scrollLeft - startOffset) / segmentWidth, 0, 1);
      return (index + segmentProgress) / lastIndex;
    }

    return 1;
  }

  function sync(instance) {
    const slides = getSlides(instance);
    const slideCount = Math.max(slides.length, 1);
    const progress = getInterpolatedProgress(instance, slides);

    instance.root.hidden = !mobileMql.matches;
    instance.progress.style.setProperty('--n', String(slideCount));
    instance.progress.style.setProperty('--p', String(progress));
  }

  function scheduleSync(instance) {
    if (instance.frameId) return;

    instance.frameId = window.requestAnimationFrame(() => {
      instance.frameId = null;
      sync(instance);
    });
  }

  function scrollToIndex(instance, index) {
    const slides = getSlides(instance);
    if (!slides.length) return;

    const wrappedIndex = ((index % slides.length) + slides.length) % slides.length;
    const targetSlide = slides[wrappedIndex];
    if (!targetSlide) return;

    instance.scroller.scrollTo({ left: targetSlide.offsetLeft, behavior: 'auto' });
    scheduleSync(instance);
  }

  function resetTouchState(instance) {
    instance.touchStartX = null;
    instance.touchStartIndex = null;
  }

  function initMobileGallery(root) {
    if (instances.has(root)) {
      scheduleSync(instances.get(root));
      return;
    }

    const mediaGallery = root.closest('media-gallery');
    const viewer = mediaGallery?.querySelector('[id^="GalleryViewer-"]');
    const scroller = mediaGallery?.querySelector('[data-mobile-main-scroller="true"]');
    const progress = root.querySelector('[data-mobile-progress]');

    if (!mediaGallery || !viewer || !scroller || !progress) return;

    const instance = {
      root,
      viewer,
      scroller,
      progress,
      frameId: null,
      touchStartX: null,
      touchStartIndex: null,
    };

    instance.onScroll = () => scheduleSync(instance);
    instance.onSlideChanged = () => scheduleSync(instance);

    instance.onTouchStart = (event) => {
      if (!mobileMql.matches) return;

      const touch = event.changedTouches?.[0];
      if (!touch) return;

      instance.touchStartX = touch.screenX;
      instance.touchStartIndex = getActiveIndex(instance);
    };

    instance.onTouchEnd = (event) => {
      if (!mobileMql.matches || instance.touchStartX === null) {
        resetTouchState(instance);
        return;
      }

      const touch = event.changedTouches?.[0];
      if (!touch) {
        resetTouchState(instance);
        return;
      }

      const deltaX = instance.touchStartX - touch.screenX;
      const startIndex = instance.touchStartIndex;
      const slides = getSlides(instance);
      const lastIndex = slides.length - 1;

      resetTouchState(instance);

      if (slides.length < 2 || Math.abs(deltaX) < swipeThreshold) {
        scheduleSync(instance);
        return;
      }

      if (startIndex === 0 && deltaX < 0) {
        window.setTimeout(() => scrollToIndex(instance, lastIndex), 0);
        return;
      }

      if (startIndex === lastIndex && deltaX > 0) {
        window.setTimeout(() => scrollToIndex(instance, 0), 0);
        return;
      }

      window.setTimeout(() => scheduleSync(instance), 0);
    };

    instance.onTouchCancel = () => resetTouchState(instance);
    instance.resizeObserver = new ResizeObserver(() => scheduleSync(instance));
    instance.mutationObserver = new MutationObserver(() => scheduleSync(instance));

    scroller.addEventListener('scroll', instance.onScroll, { passive: true });
    viewer.addEventListener('slideChanged', instance.onSlideChanged);
    scroller.addEventListener('touchstart', instance.onTouchStart, { passive: true });
    scroller.addEventListener('touchend', instance.onTouchEnd, { passive: true });
    scroller.addEventListener('touchcancel', instance.onTouchCancel, { passive: true });
    instance.resizeObserver.observe(scroller);
    instance.mutationObserver.observe(scroller, {
      childList: true,
      subtree: false,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    instances.set(root, instance);
    scheduleSync(instance);
  }

  function init() {
    document.querySelectorAll('[data-mobile-gallery-ui]').forEach(initMobileGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  const handleViewportChange = () => init();

  if (mobileMql.addEventListener) {
    mobileMql.addEventListener('change', handleViewportChange);
  } else if (mobileMql.addListener) {
    mobileMql.addListener(handleViewportChange);
  }
})();
